import { type RouteObject } from "react-router";

import { SelectOrgPage } from "@lp/scenes/SelectOrganisation/SelectOrganisation";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import {
  CoInvestmentFullPaths,
  CommonPaths,
  LPAuthPaths,
  VegaModulesPaths,
} from "@vega-modules/paths";
import { ActivationNextStep } from "@vega-modules/scenes/ActivationNextStep/ActivationNextStep";
import { CoInvestmentsLPLoginBannerItems } from "@vega-modules/scenes/CoInvestmentsLP/components/Login/LoginBannerItems";
import { CoInvestmentsLPWelcomeStepsTiles } from "@vega-modules/scenes/CoInvestmentsLP/components/Welcome/WelcomeStepsTiles";
import { CoInvestmentDetailsPage } from "@vega-modules/scenes/CoInvestmentsLP/Pages/CoInvestmentDetailsPage";
import { CoInvestmentPage } from "@vega-modules/scenes/CoInvestmentsLP/Pages/CoInvestmentPage";
import { CoInvestmentsListPage } from "@vega-modules/scenes/CoInvestmentsLP/Pages/CoInvestmentsListPage";
import { CoInvestNotificationsPage } from "@vega-modules/scenes/CoInvestmentsLP/Pages/CoInvestNotificationsPage";
import { LpConversationPage } from "@vega-modules/scenes/CoInvestmentsLP/Pages/LpConversationPage";
import { LpInsightsPage } from "@vega-modules/scenes/CoInvestmentsLP/Pages/LpInsightsPage";
import { LpMessagesPage } from "@vega-modules/scenes/CoInvestmentsLP/Pages/LpMessagesPage";
import { AuthenticatedConfigBanner } from "@vega-modules/scenes/ConfigBanner/AuthenticatedConfigBanner";
import { DealOpportunityShortCode } from "@vega-modules/scenes/DealOpportunityShortCode/DealOpportunityShortCode";
import { LogoutRoute } from "@vega-modules/scenes/Logout/LogoutRoute";
import { LPActivate } from "@vega-modules/scenes/LPAuth/Login/LPActivate";
import { LPLogin } from "@vega-modules/scenes/LPAuth/Login/LPLogin";
import { LPRegistration } from "@vega-modules/scenes/LPAuth/Registration/LPRegistration";
import { AuthCodeExchange } from "@vega-modules/scenes/OktaAuthorise/OktaAuthorise";
import { QueryRedirectHandler } from "@vega-modules/scenes/QueryRedirectHandler/QueryRedirectHandler";
import { LPPrivateRoute } from "@vega-modules/scenes/Router/LPPrivateRoute";
import { PageNotFound } from "@vega-modules/scenes/Router/PageNotFound";
import { PublicRoute } from "@vega-modules/scenes/Router/PublicRoute";
import { UserProfile } from "@vega-modules/scenes/Settings/sections/UserManagement/UserProfile/UserProfile";
import { Settings } from "@vega-modules/scenes/Settings/Settings";
import { WelcomeTasksPage } from "@vega-modules/scenes/Welcome/WelcomeTasksPage";

const LoginRoute = () => {
  const {
    global: { companyName },
  } = usePublicConfig();

  return (
    <PublicRoute>
      <LPLogin
        bannerItems={CoInvestmentsLPLoginBannerItems(companyName)}
        headerTitle={m.coInvestments_lp_login_headerTitle()}
        heading={m.coInvestments_lp_login_heading({ companyName })}
      />
    </PublicRoute>
  );
};

const WelcomeRoute = () => {
  const {
    global: { companyName },
  } = usePublicConfig();

  return (
    <LPPrivateRoute>
      <WelcomeTasksPage
        welcomeStepHeading={m.coInvestments_lp_welcome_heading({
          companyName,
        })}
        welcomeStepTiles={CoInvestmentsLPWelcomeStepsTiles(companyName)}
      />
    </LPPrivateRoute>
  );
};

const FeatureFlaggedRoute = ({
  feature,
  element,
}: {
  feature: FeatureFlag;
  element: RouteObject["element"];
}) => {
  const { featureFlag, isLoading } = useFeatureFlag();

  if (isLoading) return null;

  return featureFlag[feature] ? (
    <LPPrivateRoute>{element}</LPPrivateRoute>
  ) : (
    <PageNotFound />
  );
};

export const lpCoInvestRouteObjects: RouteObject[] = [
  {
    element: <LoginRoute />,
    path: LPAuthPaths.Login,
  },
  {
    element: (
      <PublicRoute>
        <LPActivate />
      </PublicRoute>
    ),
    path: CommonPaths.Activated,
  },
  {
    element: (
      <PublicRoute>
        <AuthCodeExchange
          portalType={FrontendPortal.COINVEST_INVESTOR_PORTAL}
        />
      </PublicRoute>
    ),
    path: CommonPaths.OktaAuth,
  },
  {
    element: (
      <PublicRoute>
        <LPRegistration />
      </PublicRoute>
    ),
    path: LPAuthPaths.InvitationCheck,
  },
  {
    element: (
      <PublicRoute>
        <LPRegistration />
      </PublicRoute>
    ),
    path: LPAuthPaths.InvestorInvestorInvitationCheck,
  },
  {
    element: (
      <LPPrivateRoute>
        <AuthenticatedConfigBanner />
        <CoInvestmentsListPage />
      </LPPrivateRoute>
    ),
    path: CoInvestmentFullPaths.CoInvestments,
  },
  {
    element: (
      <FeatureFlaggedRoute
        element={<CoInvestNotificationsPage />}
        feature={FeatureFlag.COINVEST_NOTIFICATIONS}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpNotifications,
  },
  {
    element: (
      <FeatureFlaggedRoute
        element={<LpInsightsPage />}
        feature={FeatureFlag.COINVEST_INSIGHTS_VD_26155}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpInsights,
  },
  {
    element: (
      <FeatureFlaggedRoute
        element={<LpMessagesPage />}
        feature={FeatureFlag.COINVEST_MESSAGING_VD_28584}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpMessages,
  },
  {
    element: (
      <FeatureFlaggedRoute
        element={<LpConversationPage />}
        feature={FeatureFlag.COINVEST_MESSAGING_VD_28584}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpConversation,
  },
  {
    element: (
      <LPPrivateRoute>
        <CoInvestmentPage />
      </LPPrivateRoute>
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpOverview,
  },
  {
    element: (
      <LPPrivateRoute>
        <CoInvestmentPage />
      </LPPrivateRoute>
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpDataRoom,
  },
  {
    element: (
      <LPPrivateRoute>
        <CoInvestmentPage />
      </LPPrivateRoute>
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpTerms,
  },
  {
    element: (
      <FeatureFlaggedRoute
        element={<CoInvestmentPage />}
        feature={FeatureFlag.COINVEST_REPORTING_VD_26152}
      />
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpPerformanceReport,
  },
  {
    element: (
      <LPPrivateRoute>
        <CoInvestmentDetailsPage />
      </LPPrivateRoute>
    ),
    path: CoInvestmentFullPaths.CoInvestmentLpDetails,
  },
  {
    element: <DealOpportunityShortCode />,
    path: CoInvestmentFullPaths.CoInvestmentsShortCode,
  },
  {
    element: <WelcomeRoute />,
    path: CommonPaths.Welcome,
  },
  {
    element: (
      <LPPrivateRoute>
        <SelectOrgPage />
      </LPPrivateRoute>
    ),
    path: CommonPaths.SelectOrg,
  },
  {
    element: (
      <LPPrivateRoute>
        <Settings />
      </LPPrivateRoute>
    ),
    path: VegaModulesPaths.SettingsRoot,
  },
  {
    element: (
      <LPPrivateRoute>
        <UserProfile />
      </LPPrivateRoute>
    ),
    path: VegaModulesPaths.SettingsUser,
  },
  {
    element: (
      <LPPrivateRoute>
        <CoInvestmentsListPage />
      </LPPrivateRoute>
    ),
    path: CommonPaths.Home,
  },
  {
    element: <LogoutRoute source={FrontendPortal.COINVEST_INVESTOR_PORTAL} />,
    path: CommonPaths.Logout,
  },
  { element: <QueryRedirectHandler />, path: CommonPaths.Redirect },
  {
    element: <ActivationNextStep portal={FrontendPortal.LP_PORTAL} />,
    path: CommonPaths.ActivationNextStep,
  },
  {
    element: <ActivationNextStep portal={FrontendPortal.LP_PORTAL} />,
    path: CommonPaths.RegisterOkta,
  },
  { element: <PageNotFound />, path: "*" },
];
